import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';
import { useConfirm } from '../../components/ConfirmDialog';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const STATUS_OPTIONS = [
    { value: '', label: 'Any status' },
    { value: 'open', label: 'Open' },
    { value: 'claimed', label: 'Waiting on admin' },
    { value: 'nominated', label: 'Waiting on tutor' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
    { value: 'resolved', label: 'Resolved' },
];

const STATUS_COLOR = {
    open: 'warning',
    claimed: 'info',
    nominated: 'secondary',
    approved: 'success',
    declined: 'default',
    resolved: 'default',
};

// Human labels for the row chip so "nominated" and "claimed" become
// self-explanatory ("Waiting on tutor", "Waiting on admin").
const STATUS_LABEL = {
    open: 'Open',
    claimed: 'Waiting on admin',
    nominated: 'Waiting on tutor',
    approved: 'Approved',
    declined: 'Declined',
    resolved: 'Resolved',
};

const tutorName = (t) => `${t?.firstName || ''} ${t?.surname || ''}`.trim();
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');

export default function CoverRequestsPage() {
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [tutors, setTutors] = useState([]);
    const [assignDialog, setAssignDialog] = useState(null); // { id, requesterId, lessonLabel }
    const [assignTutorId, setAssignTutorId] = useState('');
    // Default the "wait for tutor confirmation" toggle to ON — per Meera's
    // preference, admin usually wants the tutor to accept the request
    // rather than being force-assigned.
    const [assignWait, setAssignWait] = useState(true);
    const [assignBusy, setAssignBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = status ? `?status=${status}` : '';
            const { data } = await axios.get(`${API_URL}/api/admin/cover-requests${params}`, authHeaders());
            setItems(data || []);
            // Let the sidebar badge refresh instantly rather than waiting
            // for its 60s poll.
            window.dispatchEvent(new Event('cover-requests:changed'));
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    useEffect(() => {
        axios
            .get(`${API_URL}/api/admin/accounts?role=teacher&limit=200`, authHeaders())
            .then((r) => setTutors(r.data?.items || []))
            .catch(() => {});
    }, []);

    const openAssign = (cw) => {
        setAssignDialog({
            id: cw._id,
            requesterId: cw.requestedByTutorId?._id || cw.requestedByTutorId,
            lessonLabel: `${cw.tutoringClassId?.name || 'Lesson'} · ${fmtDate(cw.lessonId?.date)} ${cw.lessonId?.startsAt || ''}`,
        });
        setAssignTutorId('');
        setAssignWait(true);
    };
    const saveAssign = async () => {
        if (!assignDialog?.id || !assignTutorId) return;
        setAssignBusy(true);
        try {
            const endpoint = assignWait ? 'nominate' : 'assign';
            await axios.post(
                `${API_URL}/api/admin/cover-requests/${assignDialog.id}/${endpoint}`,
                { tutorId: assignTutorId },
                authHeaders()
            );
            setMessage(
                assignWait
                    ? 'Cover request sent. The covering tutor has been notified — the lesson stays with the current tutor until they accept.'
                    : 'Cover assigned. Both tutors have been notified by email and in-app message.'
            );
            setAssignDialog(null);
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setAssignBusy(false);
        }
    };

    const approve = async (id) => {
        if (!(await confirm({ title: 'Approve cover', message: 'The lesson tutor will be swapped to the claimer.', confirmLabel: 'Approve' }))) return;
        try {
            await axios.post(`${API_URL}/api/admin/cover-requests/${id}/approve`, {}, authHeaders());
            setMessage('Cover approved.');
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };
    const decline = async (id) => {
        if (!(await confirm({ title: 'Decline cover request', message: 'Decline this cover request?', confirmLabel: 'Decline', danger: true }))) return;
        try {
            await axios.post(`${API_URL}/api/admin/cover-requests/${id}/decline`, {}, authHeaders());
            setMessage('Declined.');
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };
    const resolve = async (id) => {
        try {
            await axios.post(`${API_URL}/api/admin/cover-requests/${id}/resolve`, {}, authHeaders());
            load();
        } catch {}
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Cover requests</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                        Tutors raise a cover request when they can't teach a scheduled lesson. Admin
                        finds a replacement, and once approved the lesson's tutor is swapped so the
                        register and class emails go to the right person.
                    </Typography>
                    
                </Box>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>How a cover request gets filled — three paths:</strong>
                            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                <li>
                                    <strong>Another tutor claims it.</strong> When a tutor raises the
                                    request, all other tutors get an in-app message. If one of them
                                    claims it, the request moves to <em>Waiting on admin</em> and you
                                    press <strong>Approve</strong> to swap the lesson tutor over.
                                </li>
                                <li>
                                    <strong>You send a cover request to a specific tutor</strong>{' '}
                                    (toggle <em>ON</em> in the Assign dialog — the default). The chosen
                                    tutor receives an in-app message and an email with Accept / Decline
                                    from their portal. The lesson stays with the <em>original</em> tutor
                                    until the covering tutor accepts. Status shows{' '}
                                    <em>Waiting on tutor</em>.
                                </li>
                                <li>
                                    <strong>You assign a tutor directly, no confirmation</strong>{' '}
                                    (toggle <em>OFF</em> in the Assign dialog). The covering tutor is
                                    stamped onto the lesson immediately. Both the original tutor and
                                    the covering tutor are notified by in-app message and email.
                                </li>
                            </ul>
                            <br />
                            <strong>Status chips:</strong> <em>Open</em> (waiting for a claim or an
                            assignment) → <em>Waiting on admin</em> (a tutor has claimed, needs your
                            approval) → <em>Waiting on tutor</em> (you nominated someone, waiting
                            for them to accept) → <em>Approved</em> → <em>Resolved</em>. <em>Declined</em>{' '}
                            means admin turned it down.
                            <br /><br />
                            <strong>Notifications you (admin) get:</strong> an email the moment a
                            tutor raises the request, plus a red badge next to <em>Cover requests</em>{' '}
                            in the sidebar that shows the number of currently open requests. The
                            badge refreshes instantly after any admin action rather than waiting for
                            the next poll.
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card sx={{ p: 2, mb: 3 }}>
                <TextField
                    select size="small" label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{ minWidth: 160 }}
                >
                    {STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.value || 'any'} value={o.value}>{o.label}</MenuItem>
                    ))}
                </TextField>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Lesson</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>Raised by</TableCell>
                                <TableCell>Claimed by</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((cw) => (
                                <TableRow key={cw._id}>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {fmtDate(cw.lessonId?.date)} · {cw.lessonId?.startsAt}
                                        </Typography>
                                        {cw.reason && (
                                            <Typography variant="caption" color="text.secondary">"{cw.reason}"</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{cw.tutoringClassId?.name || '—'}</TableCell>
                                    <TableCell>{tutorName(cw.requestedByTutorId)}</TableCell>
                                    <TableCell>{tutorName(cw.claimedByTutorId) || '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={STATUS_LABEL[cw.status] || cw.status}
                                            color={STATUS_COLOR[cw.status] || 'default'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {cw.status === 'open' && (
                                            <>
                                                <Button size="small" variant="contained" onClick={() => openAssign(cw)}>Assign tutor</Button>
                                                <Button size="small" onClick={() => decline(cw._id)}>Decline</Button>
                                            </>
                                        )}
                                        {cw.status === 'claimed' && (
                                            <>
                                                <Button size="small" variant="contained" onClick={() => approve(cw._id)}>Approve</Button>
                                                <Button size="small" onClick={() => openAssign(cw)}>Reassign</Button>
                                                <Button size="small" onClick={() => decline(cw._id)}>Decline</Button>
                                            </>
                                        )}
                                        {cw.status === 'nominated' && (
                                            <>
                                                <Button size="small" onClick={() => openAssign(cw)}>Re-nominate</Button>
                                                <Button size="small" onClick={() => decline(cw._id)}>Cancel</Button>
                                            </>
                                        )}
                                        {cw.status === 'approved' && (
                                            <>
                                                <Button size="small" onClick={() => openAssign(cw)}>Reassign</Button>
                                                <Button size="small" onClick={() => resolve(cw._id)}>Mark resolved</Button>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No cover requests right now.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>

            <Dialog open={!!assignDialog} onClose={() => setAssignDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign covering tutor</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {assignDialog && (
                            <Typography variant="body2" color="text.secondary">
                                {assignDialog.lessonLabel}
                            </Typography>
                        )}
                        <TextField
                            select size="small" label="Covering tutor"
                            value={assignTutorId}
                            onChange={(e) => setAssignTutorId(e.target.value)}
                        >
                            {tutors
                                .filter((t) => String(t._id) !== String(assignDialog?.requesterId))
                                .map((t) => (
                                    <MenuItem key={t._id} value={t._id}>
                                        {tutorName(t) || t.email}
                                    </MenuItem>
                                ))}
                            {tutors.length === 0 && (
                                <MenuItem value="" disabled>No tutor accounts found.</MenuItem>
                            )}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={assignWait}
                                    onChange={(e) => setAssignWait(e.target.checked)}
                                />
                            }
                            label="Wait for tutor's confirmation (nominate)"
                        />
                        <Typography variant="caption" color="text.secondary">
                            {assignWait ? (
                                <>
                                    The covering tutor will receive a request to cover this lesson.
                                    The lesson will stay with <strong>the current assigned tutor</strong>{' '}
                                    until the covering tutor accepts.
                                </>
                            ) : (
                                <>
                                    <strong>No confirmation is required.</strong> The covering tutor
                                    will be assigned to this lesson immediately. The{' '}
                                    <strong>current assigned tutor</strong> and the{' '}
                                    <strong>covering tutor</strong> will both be notified.
                                </>
                            )}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialog(null)} disabled={assignBusy}>Cancel</Button>
                    <Button
                        onClick={saveAssign}
                        variant="contained"
                        disabled={!assignTutorId || assignBusy}
                    >
                        {assignBusy
                            ? (assignWait ? 'Sending…' : 'Assigning…')
                            : (assignWait ? 'Send cover request' : 'Assign & approve')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
